// src/pages/FieldGroupDetail.jsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppShell from "../components/AppShell.jsx";
import MapboxFieldDraw from "../components/MapboxFieldDraw.jsx";
import FieldShapeIcon from "../components/FieldShapeIcon.jsx";
import { apiFetch, getStoredUser, safeReadError } from "../api/api.js";

/* =========================
   helpers
   ========================= */

function formatArea(v) {
  const n = Number(v || 0);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
}

function shortText(value, max = 140) {
  const s = String(value || "").trim();
  if (!s) return "";
  if (s.length <= max) return s;
  return `${s.slice(0, max)}...`;
}

function parseMaybeJson(value) {
  if (!value) return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function getGeometryOnly(value) {
  const g = parseMaybeJson(value);
  if (!g) return null;
  if (g.type === "Feature" && g.geometry) return g.geometry;
  return g;
}

function getFieldGeometry(field) {
  return (
    getGeometryOnly(field?.geometry) ??
    getGeometryOnly(field?.geometryJson) ??
    getGeometryOnly(field?.contour) ??
    getGeometryOnly(field?.shape) ??
    getGeometryOnly(field?.polygon) ??
    getGeometryOnly(field?.coordinates) ??
    null
  );
}

function normalizeField(field) {
  const area =
    field?.areaHa ??
    field?.area ??
    field?.totalArea ??
    field?.hectares ??
    field?.size ??
    0;

  const note =
    field?.note ??
    field?.description ??
    field?.comment ??
    "";

  const name =
    field?.name ??
    field?.title ??
    `Поле #${field?.id ?? "—"}`;

  const geometry = getFieldGeometry(field);
  const contourExists = !!geometry;

  return {
    ...field,
    areaHa: Number(area || 0),
    note: String(note || ""),
    name: String(name || ""),
    geometry,
    contourExists,
  };
}

export default function FieldGroupDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [group, setGroup] = useState(null);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);

  const [q, setQ] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [fName, setFName] = useState("");
  const [fArea, setFArea] = useState("");
  const [fNote, setFNote] = useState("");
  const [polyFeature, setPolyFeature] = useState(null);
  const [creating, setCreating] = useState(false);
  const [formErrs, setFormErrs] = useState({});

  const [msg, setMsg] = useState(null);

  useEffect(() => {
    const u = getStoredUser();
    if (!u) {
      navigate("/login");
      return;
    }
    setUser(u);
  }, [navigate]);

  function toast(type, text) {
    setMsg({ type, text });
    window.clearTimeout(toast._t);
    toast._t = window.setTimeout(() => setMsg(null), 2600);
  }

  function fieldGroupIdOf(f) {
    return String(f.fieldGroupId ?? f.groupId ?? f.fieldGroup?.id ?? "");
  }

  const groupFields = useMemo(() => {
    const gid = String(id);
    return (fields || [])
      .filter((f) => fieldGroupIdOf(f) === gid)
      .map(normalizeField)
      .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "uk"));
  }, [fields, id]);

  const filteredFields = useMemo(() => {
    const s = String(q || "").trim().toLowerCase();
    if (!s) return groupFields;

    return groupFields.filter((f) => {
      const haystack = [
        f.name,
        f.note,
        String(f.id ?? ""),
        f.geometry?.type || "",
        f.contourExists ? "контур геометрія polygon" : "без контуру",
      ]
        .join(" ")
        .toLowerCase();

      return haystack.includes(s);
    });
  }, [groupFields, q]);

  const stats = useMemo(() => {
    const totalArea = groupFields.reduce((acc, f) => acc + Number(f.areaHa || 0), 0);
    const contourCount = groupFields.filter((f) => f.contourExists).length;

    return {
      fieldsCount: groupFields.length,
      totalArea,
      contourCount,
      withoutContour: Math.max(0, groupFields.length - contourCount),
    };
  }, [groupFields]);

  async function load() {
    if (!user) return;
    setLoading(true);

    try {
      const resG = await apiFetch(user, "/api/field-groups", { method: "GET" }, navigate);
      if (!resG.ok) {
        const e = await safeReadError(resG);
        toast("err", e || `Помилка груп (${resG.status})`);
        setGroup(null);
        return;
      }

      const gArr = (await resG.json().catch(() => [])) || [];
      const g = Array.isArray(gArr) ? gArr.find((x) => String(x.id) === String(id)) : null;
      setGroup(g || null);

      const resF = await apiFetch(user, "/api/fields", { method: "GET" }, navigate);
      if (!resF.ok) {
        const e = await safeReadError(resF);
        toast("err", e || `Помилка полів (${resF.status})`);
        setFields([]);
        return;
      }

      const fArr = (await resF.json().catch(() => [])) || [];
      setFields(Array.isArray(fArr) ? fArr : []);
    } catch (e) {
      toast("err", e?.message || "Помилка завантаження");
      setGroup(null);
      setFields([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!user) return;
    load().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, id]);

  function openCreate() {
    setFName("");
    setFArea("");
    setFNote("");
    setPolyFeature(null);
    setFormErrs({});
    setCreateOpen(true);
  }

  function closeCreate() {
    if (creating) return;
    setCreateOpen(false);
  }

  function isPolygonFeature(x) {
    return !!(x && x.type === "Feature" && x.geometry && x.geometry.type === "Polygon");
  }

  async function handleCreate(e) {
    e.preventDefault();
    if (creating) return;

    const name = fName.trim();
    const note = fNote.trim();
    const areaHa = Number(String(fArea).replace(",", "."));
    const fieldGroupId = Number(id);

    const errs = {};
    if (name.length < 2) errs.name = "Назва поля — мінімум 2 символи";
    if (!Number.isFinite(areaHa) || areaHa <= 0) errs.area = "Введіть коректну площу, наприклад: 5.2";
    if (!Number.isFinite(fieldGroupId)) errs.group = "Невірна група — перезавантажте сторінку";
    if (!isPolygonFeature(polyFeature)) errs.contour = "Намалюйте контур поля на карті праворуч: натисніть ✏️ Polygon → клацайте кути → закрийте контур";
    if (Object.keys(errs).length > 0) { setFormErrs(errs); return; }
    setFormErrs({});

    setCreating(true);
    try {
      const payload = {
        name,
        areaHa,
        note,
        fieldGroupId,
        geometry: polyFeature.geometry,
      };

      const res = await apiFetch(
        user,
        "/api/fields",
        { method: "POST", body: JSON.stringify(payload) },
        navigate
      );

      if (!res.ok) {
        const t = await safeReadError(res);
        setFormErrs({ _server: t || `Не вдалося створити поле (${res.status})` });
        return;
      }

      toast("ok", "Поле створено ✅");
      setCreateOpen(false);
      await load();
    } finally {
      setCreating(false);
    }
  }

  return (
    <AppShell
      title={group?.name || `Група #${id}`}
      subtitle={group?.note ? group.note : "Поля в групі"}
      right={
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", width: "100%" }}>
          <button className="btn primary" onClick={openCreate} type="button">
            + Створити поле
          </button>
        </div>
      }
    >
      <div className="fgd-theme">
        <style>{`
          .fgd-theme{
            --fgd-panel: var(--panel, rgba(255,255,255,0.05));
            --fgd-panel2: var(--panel2, rgba(255,255,255,0.08));
            --fgd-surface: var(--surface, rgba(255,255,255,0.07));
            --fgd-soft: var(--surface-soft, rgba(255,255,255,0.04));
            --fgd-soft2: var(--surface-soft-2, rgba(255,255,255,0.06));
            --fgd-text: var(--text, #ffffff);
            --fgd-muted: var(--muted, rgba(255,255,255,0.68));
            --fgd-border: var(--border, rgba(255,255,255,0.10));
            --fgd-border-strong: var(--border-strong, rgba(255,255,255,0.18));
            --fgd-shadow: var(--shadow, 0 18px 40px rgba(0,0,0,0.18));
            --fgd-shadow-soft: var(--shadow-soft, 0 8px 18px rgba(0,0,0,0.10));
            --fgd-modal-bg: var(--modal-bg, var(--fgd-panel2));
            --fgd-modal-overlay: var(--modal-overlay, rgba(10,14,12,0.56));

            --fgd-ok-bg: rgba(34,197,94,0.12);
            --fgd-ok-br: rgba(34,197,94,0.32);
            --fgd-warn-bg: rgba(251,191,36,0.12);
            --fgd-warn-br: rgba(251,191,36,0.30);
            --fgd-danger-bg: rgba(239,68,68,0.12);
            --fgd-danger-br: rgba(239,68,68,0.32);
          }

          .fgd-theme *{
            box-sizing: border-box;
          }

          .fgd-topbar{
            margin-top: 14px;
            display: flex;
            align-items: center;
            justify-content: flex-start;
            gap: 10px;
            flex-wrap: wrap;
          }

          .fgd-backBtn{
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            min-height: 44px;
            padding: 0 16px;
            border-radius: 14px;
            border: 1px solid var(--fgd-border);
            background: var(--fgd-panel);
            color: var(--fgd-text);
            font-weight: 900;
            cursor: pointer;
            box-shadow: var(--fgd-shadow-soft);
            transition: .14s ease;
            max-width: 100%;
          }

          .fgd-backBtn:hover{
            transform: translateY(-1px);
            border-color: var(--fgd-border-strong);
          }

          .fgd-toast{
            position: fixed;
            right: 16px;
            top: 16px;
            z-index: 999;
            padding: 11px 14px;
            border-radius: 16px;
            border: 1px solid var(--fgd-border);
            background: var(--fgd-panel2);
            box-shadow: var(--fgd-shadow);
            backdrop-filter: blur(12px);
            max-width: min(420px, calc(100vw - 24px));
            color: var(--fgd-text);
          }

          .fgd-panel{
            margin-top: 14px;
            padding: 18px;
          }

          .fgd-head{
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 12px;
            flex-wrap: wrap;
          }

          .fgd-title{
            font-size: clamp(20px, 2vw, 26px);
            font-weight: 1000;
            color: var(--fgd-text);
            line-height: 1.1;
            word-break: break-word;
          }

          .fgd-sub{
            margin-top: 4px;
            color: var(--fgd-muted);
            font-size: 13px;
            font-weight: 700;
            line-height: 1.4;
          }

          .fgd-stats{
            margin-top: 16px;
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 12px;
          }

          .fgd-statCard{
            border-radius: 18px;
            border: 1px solid var(--fgd-border);
            background: linear-gradient(180deg, var(--fgd-panel2), var(--fgd-panel));
            box-shadow: var(--fgd-shadow-soft);
            padding: 14px 16px;
            position: relative;
            overflow: hidden;
            min-width: 0;
          }

          .fgd-statCard::before{
            content: "";
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            width: 4px;
            background: rgba(34,197,94,0.45);
          }

          .fgd-statLabel{
            font-size: 12px;
            font-weight: 900;
            color: var(--fgd-muted);
            text-transform: uppercase;
            letter-spacing: .04em;
            line-height: 1.35;
          }

          .fgd-statValue{
            margin-top: 8px;
            font-size: clamp(20px, 2vw, 24px);
            font-weight: 1000;
            color: var(--fgd-text);
            line-height: 1.1;
            word-break: break-word;
          }

          .fgd-toolbar{
            margin-top: 16px;
            display: grid;
            grid-template-columns: 1fr;
            gap: 12px;
            align-items: stretch;
          }

          .fgd-searchBox{
            border-radius: 20px;
            border: 1px solid var(--fgd-border);
            background: var(--fgd-panel);
            box-shadow: var(--fgd-shadow-soft);
            padding: 14px;
          }

          .fgd-boxTitle{
            font-size: 13px;
            font-weight: 950;
            color: var(--fgd-muted);
            text-transform: uppercase;
            letter-spacing: .04em;
            line-height: 1.35;
          }

          .fgd-searchRow{
            margin-top: 10px;
            display: flex;
            gap: 10px;
            align-items: center;
          }

          .fgd-input,
          .fgd-textarea{
            width: 100%;
            min-height: 46px;
            border-radius: 16px;
            border: 1px solid var(--fgd-border);
            background: var(--fgd-surface);
            color: var(--fgd-text);
            padding: 12px 14px;
            outline: none;
            font-weight: 800;
            transition: border .15s ease, box-shadow .15s ease, background .15s ease;
            box-sizing: border-box;
            font-size: 15px;
          }

          .fgd-textarea{
            min-height: 110px;
            resize: vertical;
            font-family: inherit;
            line-height: 1.45;
          }

          .fgd-input::placeholder,
          .fgd-textarea::placeholder{
            color: var(--fgd-muted);
          }

          .fgd-input:focus,
          .fgd-textarea:focus{
            border-color: var(--fgd-ok-br);
            box-shadow: 0 0 0 3px rgba(34,197,94,0.10);
          }

          .fgd-clearBtn{
            height: 46px;
            min-width: 46px;
            padding: 0 14px;
            border-radius: 14px;
            border: 1px solid var(--fgd-border);
            background: var(--fgd-panel2);
            color: var(--fgd-text);
            font-weight: 950;
            cursor: pointer;
            flex: 0 0 auto;
          }

          .fgd-list{
            margin-top: 16px;
            display: grid;
            gap: 14px;
          }

          .fgd-rowBtn{
            appearance: none;
            border: 0;
            background: transparent;
            padding: 0;
            margin: 0;
            width: 100%;
            text-align: left;
            cursor: pointer;
            font: inherit;
          }

          .fgd-card{
            border-radius: 22px;
            border: 1px solid var(--fgd-border);
            background: linear-gradient(180deg, var(--fgd-panel2), var(--fgd-panel));
            box-shadow: var(--fgd-shadow-soft);
            overflow: hidden;
            transition: transform .12s ease, border .12s ease, box-shadow .12s ease;
          }

          .fgd-rowBtn:hover .fgd-card{
            transform: translateY(-2px);
            border-color: var(--fgd-border-strong);
            box-shadow: var(--fgd-shadow);
          }

          .fgd-rowBtn:active .fgd-card{
            transform: scale(.996);
          }

          .fgd-cardInner{
            padding: 16px;
          }

          .fgd-cardTop{
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            gap: 14px;
            flex-wrap: wrap;
          }

          .fgd-cardLeft{
            display: flex;
            gap: 14px;
            align-items: center;
            min-width: 0;
            flex: 1 1 420px;
          }

          .fgd-iconWrap{
            width: 56px;
            height: 56px;
            flex: 0 0 56px;
            border-radius: 18px;
            display: grid;
            place-items: center;
            border: 1px solid var(--fgd-border);
            background: linear-gradient(180deg, var(--fgd-soft), var(--fgd-panel));
          }

          .fgd-name{
            font-size: clamp(17px, 1.8vw, 20px);
            font-weight: 1000;
            line-height: 1.12;
            color: var(--fgd-text);
            word-break: break-word;
          }

          .fgd-id{
            margin-top: 5px;
            color: var(--fgd-muted);
            font-size: 13px;
            font-weight: 800;
            word-break: break-word;
          }

          .fgd-note{
            margin-top: 10px;
            color: var(--fgd-text);
            line-height: 1.5;
            font-size: 14px;
            font-weight: 750;
            word-break: break-word;
          }

          .fgd-noteEmpty{
            margin-top: 10px;
            color: var(--fgd-muted);
            font-weight: 700;
            font-size: 14px;
          }

          .fgd-meta{
            display: flex;
            align-items: center;
            gap: 10px;
            flex-wrap: wrap;
            justify-content: flex-end;
            max-width: 100%;
          }

          .fgd-badge{
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            min-height: 38px;
            padding: 8px 12px;
            border-radius: 999px;
            border: 1px solid var(--fgd-border);
            background: var(--fgd-panel2);
            color: var(--fgd-text);
            font-size: 13px;
            font-weight: 950;
            white-space: normal;
            text-align: center;
            line-height: 1.25;
            max-width: 100%;
          }

          .fgd-badge.ok{
            background: var(--fgd-ok-bg);
            border-color: var(--fgd-ok-br);
          }

          .fgd-badge.warn{
            background: var(--fgd-warn-bg);
            border-color: var(--fgd-warn-br);
          }

          .fgd-cardBottom{
            margin-top: 14px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 10px;
            flex-wrap: wrap;
          }

          .fgd-helper{
            color: var(--fgd-muted);
            font-size: 13px;
            font-weight: 800;
            line-height: 1.4;
          }

          .fgd-openText{
            color: var(--fgd-text);
            font-weight: 950;
            white-space: nowrap;
          }

          .fgd-empty{
            margin-top: 16px;
            padding: 18px 16px;
            border-radius: 18px;
            border: 1px dashed var(--fgd-border-strong);
            background: var(--fgd-soft);
            color: var(--fgd-muted);
            font-weight: 850;
            line-height: 1.45;
          }

          .fgd-modal{
            position: fixed;
            inset: 0;
            background: rgba(6,12,10,0.72);
            backdrop-filter: blur(14px);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 5000;
            padding: 16px;
          }

          .fgd-modalCard{
            width: min(1120px, 96vw);
            max-height: calc(100vh - 32px);
            overflow: auto;
            border-radius: 24px;
            border: 1px solid rgba(255,255,255,0.09);
            border-top: 3px solid rgba(34,197,94,0.55);
            background: #0d1410;
            color: var(--fgd-text);
            box-shadow:
              0 40px 100px rgba(0,0,0,0.65),
              0 0 0 1px rgba(34,197,94,0.06);
            display: flex;
            flex-direction: column;
          }

          /* ── Modal header ── */
          .fgd-modalHead{
            padding: 18px 20px 16px;
            border-bottom: 1px solid rgba(255,255,255,0.07);
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 14px;
            background: linear-gradient(180deg,
              rgba(34,197,94,0.09) 0%,
              transparent 100%);
            position: sticky;
            top: 0;
            z-index: 2;
            backdrop-filter: blur(16px);
            flex-shrink: 0;
          }

          .fgd-modalHeadLeft{
            display: flex;
            align-items: center;
            gap: 14px;
            min-width: 0;
          }

          .fgd-modalHeroIcon{
            width: 46px;
            height: 46px;
            border-radius: 14px;
            background: linear-gradient(135deg,
              rgba(34,197,94,0.20) 0%,
              rgba(16,185,129,0.12) 100%);
            border: 1px solid rgba(34,197,94,0.28);
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 22px;
            flex: 0 0 auto;
            box-shadow: 0 4px 16px rgba(34,197,94,0.12);
          }

          .fgd-modalTitle{
            font-size: 20px;
            font-weight: 1000;
            color: #fff;
            line-height: 1.15;
            letter-spacing: -.01em;
          }

          .fgd-modalSub{
            margin-top: 3px;
            color: rgba(255,255,255,0.40);
            font-size: 12.5px;
            font-weight: 750;
            line-height: 1.4;
            word-break: break-word;
          }

          .fgd-closeBtn{
            width: 36px;
            height: 36px;
            border-radius: 10px;
            border: 1px solid rgba(255,255,255,0.10);
            background: rgba(255,255,255,0.05);
            color: rgba(255,255,255,0.50);
            font-size: 15px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: .15s ease;
            flex: 0 0 auto;
            line-height: 1;
          }

          .fgd-closeBtn:hover{
            background: rgba(255,255,255,0.10);
            color: #fff;
            border-color: rgba(255,255,255,0.20);
          }

          /* ── Modal body ── */
          .fgd-modalBody{
            padding: 16px;
            flex: 1 1 auto;
            overflow: auto;
          }

          /* ── Modal footer ── */
          .fgd-modalFooter{
            padding: 14px 20px;
            border-top: 1px solid rgba(255,255,255,0.07);
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: 10px;
            background: rgba(0,0,0,0.20);
            flex-shrink: 0;
          }

          .fgd-cancelBtn{
            height: 44px;
            padding: 0 20px;
            border-radius: 12px;
            border: 1px solid rgba(255,255,255,0.09);
            background: rgba(255,255,255,0.04);
            color: rgba(255,255,255,0.50);
            font-size: 14px;
            font-weight: 800;
            cursor: pointer;
            transition: .15s ease;
          }

          .fgd-cancelBtn:hover:not(:disabled){
            background: rgba(255,255,255,0.08);
            color: rgba(255,255,255,0.80);
            border-color: rgba(255,255,255,0.15);
          }

          .fgd-cancelBtn:disabled{ opacity:.35; cursor:default; }

          .fgd-saveBtn{
            height: 44px;
            padding: 0 24px;
            border-radius: 12px;
            border: 1px solid rgba(34,197,94,0.45);
            background: linear-gradient(135deg,
              rgba(34,197,94,0.22) 0%,
              rgba(16,185,129,0.15) 100%);
            color: #4ade80;
            font-size: 14px;
            font-weight: 950;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 8px;
            transition: .16s ease;
            letter-spacing: .01em;
          }

          .fgd-saveBtn:hover:not(:disabled){
            background: linear-gradient(135deg,
              rgba(34,197,94,0.32) 0%,
              rgba(16,185,129,0.24) 100%);
            border-color: rgba(34,197,94,0.65);
            box-shadow: 0 0 22px rgba(34,197,94,0.18);
            color: #6ee7b7;
          }

          .fgd-saveBtn:disabled{
            opacity: .32;
            cursor: default;
          }

          .fgd-saveDot{
            width: 7px;
            height: 7px;
            border-radius: 50%;
            background: currentColor;
            opacity: .7;
            flex: 0 0 auto;
          }

          /* ===== CREATE FORM LAYOUT ===== */
          .fgd-createGrid{
            display: grid;
            grid-template-columns: 340px minmax(0,1fr);
            gap: 16px;
            align-items: start;
          }

          /* ===== LEFT PANEL CARD ===== */
          .fgd-createSide{
            min-width: 0;
            border-radius: 20px;
            border: 1px solid rgba(255,255,255,0.09);
            background: linear-gradient(160deg,
              rgba(255,255,255,0.055) 0%,
              rgba(255,255,255,0.025) 100%);
            overflow: hidden;
          }

          .fgd-sideTop{
            padding: 14px 18px 13px;
            border-bottom: 1px solid rgba(255,255,255,0.07);
            display: flex;
            align-items: center;
            gap: 11px;
            background: linear-gradient(90deg,
              rgba(34,197,94,0.12) 0%,
              transparent 70%);
          }

          .fgd-sideTopIcon{
            width: 36px;
            height: 36px;
            border-radius: 11px;
            background: rgba(34,197,94,0.15);
            border: 1px solid rgba(34,197,94,0.25);
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 17px;
            flex: 0 0 auto;
          }

          .fgd-sideTopTitle{
            font-size: 14px;
            font-weight: 980;
            color: #fff;
            line-height: 1.2;
          }

          .fgd-sideTopSub{
            margin-top: 2px;
            font-size: 11px;
            font-weight: 700;
            color: rgba(255,255,255,0.42);
            line-height: 1.3;
          }

          .fgd-sideBody{
            padding: 16px 18px 18px;
            display: flex;
            flex-direction: column;
            gap: 13px;
          }

          /* ===== FORM FIELDS ===== */
          .fgd-formGroup{
            display: flex;
            flex-direction: column;
            gap: 6px;
          }

          .fgd-fieldLabel{
            display: flex;
            align-items: center;
            justify-content: space-between;
            font-size: 10.5px;
            font-weight: 950;
            color: rgba(255,255,255,0.38);
            text-transform: uppercase;
            letter-spacing: .08em;
          }

          .fgd-fieldLabelIcon{ display: none; }

          .fgd-charCount{
            font-size: 10px;
            font-weight: 700;
            color: rgba(255,255,255,0.20);
            font-variant-numeric: tabular-nums;
            letter-spacing: 0;
            text-transform: none;
          }

          /* ===== INPUTS ===== */
          .fgd-input,
          .fgd-textarea{
            width: 100%;
            border-radius: 12px;
            border: 1px solid rgba(255,255,255,0.10);
            background: rgba(0,0,0,0.28);
            color: #fff;
            outline: none;
            font-family: inherit;
            font-weight: 700;
            transition: border .18s ease, box-shadow .18s ease, background .18s ease;
            box-sizing: border-box;
          }

          .fgd-input{
            height: 46px;
            padding: 0 14px;
            font-size: 15px;
          }

          .fgd-textarea{
            min-height: 72px;
            padding: 11px 14px;
            font-size: 14px;
            resize: none;
            line-height: 1.5;
          }

          .fgd-input::placeholder,
          .fgd-textarea::placeholder{
            color: rgba(255,255,255,0.20);
            font-weight: 600;
          }

          .fgd-input:focus,
          .fgd-textarea:focus{
            border-color: rgba(34,197,94,0.60);
            box-shadow: 0 0 0 3px rgba(34,197,94,0.10);
            background: rgba(0,0,0,0.35);
          }

          /* area input with suffix */
          .fgd-inputRow{
            position: relative;
            display: flex;
            align-items: center;
          }

          .fgd-inputRow .fgd-input{
            padding-right: 42px;
          }

          .fgd-inputUnit{
            position: absolute;
            right: 13px;
            font-size: 12px;
            font-weight: 900;
            color: rgba(255,255,255,0.30);
            pointer-events: none;
            letter-spacing: .03em;
          }

          /* ===== CONTOUR STATUS ===== */
          .fgd-contourSection{
            display: flex;
            flex-direction: column;
            gap: 0;
          }

          .fgd-statusRow{
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .fgd-statusChip{
            flex: 1 1 auto;
            height: 44px;
            border-radius: 12px;
            border: 1.5px solid;
            padding: 0 14px;
            font-size: 13px;
            font-weight: 850;
            display: flex;
            align-items: center;
            gap: 9px;
            transition: all .22s ease;
          }

          .fgd-statusChip.ok{
            border-color: rgba(34,197,94,0.38);
            background: rgba(34,197,94,0.10);
            color: #4ade80;
          }

          .fgd-statusChip.warn{
            border-color: rgba(234,179,8,0.28);
            background: rgba(234,179,8,0.07);
            color: rgba(234,179,8,0.85);
          }

          .fgd-statusDot{
            width: 8px;
            height: 8px;
            border-radius: 50%;
            flex: 0 0 auto;
          }

          .fgd-statusChip.ok .fgd-statusDot{
            background: #4ade80;
            box-shadow: 0 0 7px rgba(74,222,128,0.55);
          }

          .fgd-statusChip.warn .fgd-statusDot{
            background: rgba(234,179,8,0.9);
            animation: fgdPulse 1.8s ease-in-out infinite;
          }

          @keyframes fgdPulse{
            0%,100%{ opacity:1; transform:scale(1); }
            50%{ opacity:.38; transform:scale(0.65); }
          }

          .fgd-trashBtn{
            width: 44px;
            height: 44px;
            border-radius: 12px;
            border: 1px solid rgba(255,255,255,0.09);
            background: rgba(0,0,0,0.22);
            color: rgba(255,255,255,0.30);
            font-size: 16px;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: .16s ease;
            flex: 0 0 auto;
          }

          .fgd-trashBtn:hover:not(:disabled){
            border-color: rgba(239,68,68,0.45);
            background: rgba(239,68,68,0.10);
            color: #f87171;
          }

          .fgd-trashBtn:disabled{
            opacity: .22;
            cursor: default;
          }

          /* ===== GUIDE ===== */
          .fgd-guide{
            border-top: 1px solid rgba(255,255,255,0.07);
            padding-top: 13px;
            display: flex;
            flex-direction: column;
            gap: 7px;
          }

          .fgd-guideTitle{
            font-size: 10px;
            font-weight: 950;
            color: rgba(255,255,255,0.25);
            text-transform: uppercase;
            letter-spacing: .09em;
            margin-bottom: 1px;
          }

          .fgd-guideItem{
            display: flex;
            align-items: center;
            gap: 10px;
          }

          .fgd-guideNum{
            width: 19px;
            height: 19px;
            border-radius: 50%;
            border: 1px solid rgba(34,197,94,0.20);
            background: rgba(34,197,94,0.07);
            color: rgba(74,222,128,0.75);
            font-size: 10px;
            font-weight: 1000;
            display: flex;
            align-items: center;
            justify-content: center;
            flex: 0 0 auto;
          }

          .fgd-guideText{
            font-size: 12px;
            font-weight: 700;
            color: rgba(255,255,255,0.38);
            line-height: 1.35;
          }

          /* ===== MAP BOX ===== */
          .fgd-mapBox{
            border-radius: 20px;
            border: 1px solid rgba(255,255,255,0.09);
            overflow: hidden;
            background: var(--fgd-panel);
            min-width: 0;
            display: flex;
            flex-direction: column;
          }

          .fgd-mapBoxHead{
            padding: 13px 16px 11px;
            border-bottom: 1px solid rgba(255,255,255,0.07);
            background: rgba(0,0,0,0.18);
          }

          .fgd-mapBoxTitle{
            font-size: 13px;
            font-weight: 950;
            color: rgba(255,255,255,0.85);
            line-height: 1.3;
          }

          .fgd-mapBoxSub{
            margin-top: 2px;
            font-size: 11px;
            font-weight: 700;
            color: rgba(255,255,255,0.30);
            line-height: 1.4;
          }

          /* ===== ACTIONS ===== */
          .fgd-actions{
            margin-top: 16px;
            display: flex;
            gap: 10px;
            justify-content: flex-end;
            flex-wrap: wrap;
          }

          /* legacy unused — kept for compat */
          .fgd-label{ display:none; }
          .fgd-miniStatus{ display:none; }
          .fgd-helpBox,.fgd-helpTitle,.fgd-stepItem,.fgd-stepNum,.fgd-stepText,.fgd-divider{ display:none; }

          /* ---------- Large tablets ---------- */
          @media (max-width: 1100px){
            .fgd-stats{
              grid-template-columns: repeat(2, minmax(0, 1fr));
            }

            .fgd-createGrid{
              grid-template-columns: 1fr;
            }
          }

          /* ---------- Tablets ---------- */
          @media (max-width: 768px){
            .fgd-panel{
              padding: 14px;
            }

            .fgd-cardInner{
              padding: 14px;
            }

            .fgd-cardLeft{
              align-items: flex-start;
            }

            .fgd-meta{
              width: 100%;
              justify-content: flex-start;
            }

            .fgd-modal{
              padding: 10px;
            }

            .fgd-modalCard{
              width: 100%;
              max-height: calc(100vh - 20px);
              border-radius: 18px;
            }
          }

          /* ---------- Phones ---------- */
          @media (max-width: 560px){
            .fgd-topbar,
            .fgd-cardTop,
            .fgd-cardBottom,
            .fgd-head,
            .fgd-searchRow,
            .fgd-actions,
            .fgd-modalHead{
              flex-direction: column;
              align-items: stretch;
            }

            .fgd-panel{
              margin-top: 12px;
              padding: 12px;
            }

            .fgd-searchBox{
              padding: 12px;
              border-radius: 16px;
            }

            .fgd-stats{
              grid-template-columns: 1fr;
            }

            .fgd-statCard{
              padding: 14px;
            }

            .fgd-card{
              border-radius: 18px;
            }

            .fgd-cardInner{
              padding: 12px;
            }

            .fgd-cardLeft{
              flex-direction: row;
              align-items: flex-start;
              gap: 12px;
            }

            .fgd-iconWrap{
              width: 48px;
              height: 48px;
              flex: 0 0 48px;
              border-radius: 14px;
            }

            .fgd-meta{
              justify-content: flex-start;
            }

            .fgd-badge{
              width: 100%;
              justify-content: flex-start;
              border-radius: 14px;
            }

            .fgd-clearBtn,
            .fgd-backBtn,
            .fgd-actions .btn,
            .fgd-modalHead .btn{
              width: 100%;
            }

            .fgd-openText{
              white-space: normal;
            }

            .fgd-modal{
              padding: 0;
              align-items: stretch;
              justify-content: stretch;
            }

            .fgd-modalCard{
              width: 100vw;
              max-height: 100vh;
              min-height: 100vh;
              border-radius: 0;
              border-left: 0;
              border-right: 0;
            }

            .fgd-modalBody{
              padding: 12px;
            }

            .fgd-mapBox{
              border-radius: 16px;
            }
          }

          /* ---------- Very small phones ---------- */
          @media (max-width: 380px){
            .fgd-title{
              font-size: 18px;
            }

            .fgd-name{
              font-size: 16px;
            }

            .fgd-id,
            .fgd-helper,
            .fgd-note,
            .fgd-noteEmpty{
              font-size: 13px;
            }

            .fgd-input,
            .fgd-textarea{
              padding: 11px 12px;
              font-size: 14px;
            }

            .fgd-statValue{
              font-size: 20px;
            }

            .fgd-badge{
              font-size: 12px;
              padding: 8px 10px;
            }
          }

          /* ---- Mobile bottom-sheet modals + touch targets ---- */
          @media (max-width: 760px) {
            .fgd-modal {
              padding: 0;
              align-items: flex-end;
              justify-content: flex-end;
            }
            .fgd-modalCard {
              width: 100vw;
              max-height: 96vh;
              min-height: auto;
              border-radius: 22px 22px 0 0;
              border-left: none;
              border-right: none;
              border-bottom: none;
            }
            .fgd-input,
            .fgd-textarea,
            .fgd-select {
              font-size: 16px;
              min-height: 48px;
            }
            .btn,
            .fgd-backBtn,
            .fgd-clearBtn {
              min-height: 44px;
            }

            /* Full-width card action buttons */
            .fgd-cardBottom {
              flex-direction: column;
              align-items: stretch;
            }
            .fgd-cardBottom .btn {
              width: 100%;
            }
            .fgd-actions {
              flex-wrap: wrap;
            }
            .fgd-actions .btn {
              flex: 1;
              min-width: 140px;
            }
            .fgd-head {
              flex-direction: column;
              align-items: stretch;
              gap: 10px;
            }
            .fgd-head .btn {
              width: 100%;
            }
          }
        `}</style>

        {msg && (
          <div
            className="fgd-toast"
            style={{
              borderColor:
                msg.type === "ok" ? "var(--fgd-ok-br)" : "var(--fgd-danger-br)",
            }}
          >
            <span style={{ fontWeight: 950 }}>{msg.text}</span>
          </div>
        )}

        <div className="fgd-topbar">
          <button
            className="fgd-backBtn"
            type="button"
            onClick={() => navigate("/fields/groups")}
          >
            ← До груп
          </button>
        </div>

        <div className="panel fgd-panel">
          <div className="fgd-head">
            <div style={{ minWidth: 0 }}>
              <div className="fgd-title">Поля в групі</div>
              <div className="fgd-sub">{group?.name || `Група #${id}`}</div>
            </div>
          </div>

          <div className="fgd-stats">
            <div className="fgd-statCard">
              <div className="fgd-statLabel">Усього полів</div>
              <div className="fgd-statValue">{stats.fieldsCount}</div>
            </div>

            <div className="fgd-statCard">
              <div className="fgd-statLabel">Загальна площа</div>
              <div className="fgd-statValue">{formatArea(stats.totalArea)} ha</div>
            </div>

            <div className="fgd-statCard">
              <div className="fgd-statLabel">З контуром</div>
              <div className="fgd-statValue">{stats.contourCount}</div>
            </div>

            <div className="fgd-statCard">
              <div className="fgd-statLabel">Без контуру</div>
              <div className="fgd-statValue">{stats.withoutContour}</div>
            </div>
          </div>

          <div className="fgd-toolbar">
            <div className="fgd-searchBox">
              <div className="fgd-boxTitle">Пошук по полях цієї групи</div>

              <div className="fgd-searchRow">
                <input
                  className="fgd-input"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Назва, ID, нотатка, polygon..."
                />
                {q ? (
                  <button
                    type="button"
                    className="fgd-clearBtn"
                    onClick={() => setQ("")}
                    title="Очистити пошук"
                  >
                    ✕
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="fgd-empty">Завантажую поля...</div>
          ) : filteredFields.length === 0 ? (
            <div className="fgd-empty">
              {groupFields.length === 0
                ? "Полів поки немає. Натисни + Створити поле."
                : "Нічого не знайдено по поточному пошуку."}
            </div>
          ) : (
            <div className="fgd-list">
              {filteredFields.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  className="fgd-rowBtn"
                  onClick={() => navigate(`/fields/${f.id}`)}
                >
                  <div className="fgd-card">
                    <div className="fgd-cardInner">
                      <div className="fgd-cardTop">
                        <div className="fgd-cardLeft">
                          <div className="fgd-iconWrap">
                            <FieldShapeIcon geometry={f.geometry} size={34} />
                          </div>

                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div className="fgd-name">{f.name || `Поле #${f.id}`}</div>
                            <div className="fgd-id">ID: {f.id}</div>

                            {f.note ? (
                              <div className="fgd-note">{shortText(f.note, 180)}</div>
                            ) : (
                              <div className="fgd-noteEmpty">Нотатка відсутня</div>
                            )}
                          </div>
                        </div>

                        <div className="fgd-meta">
                          <span className="fgd-badge">📏 {formatArea(f.areaHa)} ha</span>
                          <span className={`fgd-badge ${f.contourExists ? "ok" : "warn"}`}>
                            {f.contourExists ? "⛶ Контур є" : "— Без контуру"}
                          </span>
                          <span className="fgd-badge">
                            {f.geometry?.type || "Тип: —"}
                          </span>
                        </div>
                      </div>

                      <div className="fgd-cardBottom">
                        <div className="fgd-helper">
                          Натисни, щоб відкрити деталі цього поля
                        </div>
                        <div className="fgd-openText">Відкрити →</div>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {createOpen && (
          <div className="fgd-modal" role="dialog" aria-modal="true" onClick={closeCreate}>
            <div className="fgd-modalCard" onClick={(e) => e.stopPropagation()}>

              <div className="fgd-modalHead">
                <div className="fgd-modalHeadLeft">
                  <div className="fgd-modalHeroIcon">🌾</div>
                  <div style={{ minWidth: 0 }}>
                    <div className="fgd-modalTitle">Нове поле</div>
                    <div className="fgd-modalSub">
                      {group?.name ? `Група · ${group.name}` : `Група #${id}`}
                    </div>
                  </div>
                </div>
                <button
                  className="fgd-closeBtn"
                  onClick={closeCreate}
                  type="button"
                  aria-label="Закрити"
                >
                  ✕
                </button>
              </div>

              <form onSubmit={handleCreate} style={{ display: "contents" }}>
              <div className="fgd-modalBody">
                <div className="fgd-createGrid">
                  <div className="fgd-createSide">

                    {/* ── Panel header ── */}
                    <div className="fgd-sideTop">
                      <div className="fgd-sideTopIcon">🌱</div>
                      <div>
                        <div className="fgd-sideTopTitle">Деталі поля</div>
                        <div className="fgd-sideTopSub">Заповніть дані та обведіть контур</div>
                      </div>
                    </div>

                    <div className="fgd-sideBody">

                      {/* ── Name ── */}
                      <div className="fgd-formGroup">
                        <div className="fgd-fieldLabel">
                          Назва поля <span style={{ color: "#ef4444" }}>*</span>
                          <span className="fgd-charCount">{fName.length}/64</span>
                        </div>
                        <input
                          className="fgd-input"
                          value={fName}
                          onChange={(e) => { setFName(e.target.value); if (formErrs.name) setFormErrs(p => ({ ...p, name: null })); }}
                          maxLength={64}
                          autoFocus
                          placeholder="Напр: Північне поле"
                          style={formErrs.name ? { borderColor: "#ef4444", boxShadow: "0 0 0 2px rgba(239,68,68,.2)" } : {}}
                        />
                        {formErrs.name && (
                          <div style={{ color: "#ef4444", fontSize: 12, marginTop: 5, fontWeight: 600, display: "flex", alignItems: "center", gap: 5 }}>
                            ⚠️ {formErrs.name}
                          </div>
                        )}
                      </div>

                      {/* ── Area ── */}
                      <div className="fgd-formGroup">
                        <div className="fgd-fieldLabel">Площа <span style={{ color: "#ef4444" }}>*</span></div>
                        <div className="fgd-inputRow">
                          <input
                            className="fgd-input"
                            value={fArea}
                            onChange={(e) => { setFArea(e.target.value); if (formErrs.area) setFormErrs(p => ({ ...p, area: null })); }}
                            maxLength={16}
                            placeholder="0.00"
                            inputMode="decimal"
                            style={formErrs.area ? { borderColor: "#ef4444", boxShadow: "0 0 0 2px rgba(239,68,68,.2)" } : {}}
                          />
                          <span className="fgd-inputUnit">га</span>
                        </div>
                        {formErrs.area && (
                          <div style={{ color: "#ef4444", fontSize: 12, marginTop: 5, fontWeight: 600, display: "flex", alignItems: "center", gap: 5 }}>
                            ⚠️ {formErrs.area}
                          </div>
                        )}
                      </div>

                      {/* ── Note ── */}
                      <div className="fgd-formGroup">
                        <div className="fgd-fieldLabel">
                          Нотатка
                          <span className="fgd-charCount">{fNote.length}/400</span>
                        </div>
                        <textarea
                          className="fgd-textarea"
                          value={fNote}
                          onChange={(e) => setFNote(e.target.value)}
                          maxLength={400}
                          rows={3}
                          placeholder="Додаткова інформація про поле…"
                        />
                      </div>

                      {/* ── Contour status ── */}
                      <div className="fgd-contourSection">
                        <div className="fgd-statusRow">
                          <div className={`fgd-statusChip ${isPolygonFeature(polyFeature) ? "ok" : "warn"}`}>
                            <span className="fgd-statusDot" />
                            {isPolygonFeature(polyFeature)
                              ? "Контур накреслено ✅"
                              : "Контур не накреслено ⚠️"}
                          </div>
                          <button
                            className="fgd-trashBtn"
                            type="button"
                            onClick={() => { setPolyFeature(null); setFormErrs(p => ({ ...p, contour: null })); }}
                            disabled={creating || !isPolygonFeature(polyFeature)}
                            title="Очистити контур"
                          >
                            🗑
                          </button>
                        </div>
                        {formErrs.contour && !isPolygonFeature(polyFeature) && (
                          <div style={{
                            marginTop: 8, padding: "10px 12px", borderRadius: 9,
                            background: "rgba(239,68,68,.09)", border: "1px solid rgba(239,68,68,.35)",
                            color: "#dc2626", fontSize: 12, fontWeight: 600, lineHeight: 1.5,
                          }}>
                            ⚠️ {formErrs.contour}
                          </div>
                        )}
                      </div>

                      {/* ── How-to guide ── */}
                      <div className="fgd-guide">
                        <div className="fgd-guideTitle">Як намалювати</div>
                        {[
                          ["1", "✏️ Polygon — кнопка зліва на карті"],
                          ["2", "Клацай по кутах поля"],
                          ["3", "Закрий — клацни першу точку"],
                          ["4", "📍 Справа — знайди себе на карті"],
                          ["5", "🗑 Trash — скасувати контур"],
                        ].map(([n, t]) => (
                          <div className="fgd-guideItem" key={n}>
                            <span className="fgd-guideNum">{n}</span>
                            <span className="fgd-guideText">{t}</span>
                          </div>
                        ))}
                      </div>

                    </div>
                  </div>

                  <div className="fgd-mapBox">
                    <div className="fgd-mapBoxHead">
                      <div className="fgd-mapBoxTitle">Контур поля на карті</div>
                      <div className="fgd-mapBoxSub">Satellite 🛰 · Малюй Polygon ✏️ · Знайди себе 📍</div>
                    </div>
                    <MapboxFieldDraw
                      height={window.innerWidth <= 560 ? 320 : window.innerWidth <= 980 ? 380 : 460}
                      valueGeoJson={polyFeature}
                      onChangeGeoJson={setPolyFeature}
                      readonly={false}
                    />
                  </div>
                </div>
              </div>

              <div className="fgd-modalFooter" style={{ flexDirection: "column", gap: 10 }}>
                {/* ── Серверна помилка ── */}
                {formErrs._server && (
                  <div style={{
                    width: "100%", padding: "10px 14px", borderRadius: 10,
                    background: "rgba(239,68,68,.10)", border: "1px solid rgba(239,68,68,.4)",
                    color: "#dc2626", fontSize: 13, fontWeight: 600, lineHeight: 1.5,
                  }}>
                    ❌ {formErrs._server}
                  </div>
                )}

                {/* ── Чеклист вимог ── */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, width: "100%" }}>
                  {[
                    { ok: fName.trim().length >= 2, label: "Назва" },
                    { ok: !!(Number(String(fArea).replace(",", ".")) > 0), label: "Площа" },
                    { ok: isPolygonFeature(polyFeature), label: "Контур" },
                  ].map(({ ok, label }) => (
                    <span key={label} style={{
                      fontSize: 12, fontWeight: 700, padding: "4px 11px", borderRadius: 20,
                      background: ok ? "rgba(34,197,94,.13)" : "rgba(234,179,8,.13)",
                      border: `1px solid ${ok ? "rgba(34,197,94,.38)" : "rgba(234,179,8,.45)"}`,
                      color: ok ? "#15803d" : "#92400e",
                    }}>
                      {ok ? "✅" : "⚠️"} {label}
                    </span>
                  ))}
                </div>

                <div style={{ display: "flex", gap: 10, width: "100%" }}>
                  <button
                    className="fgd-cancelBtn"
                    type="button"
                    onClick={closeCreate}
                    disabled={creating}
                  >
                    Скасувати
                  </button>
                  <button
                    className="fgd-saveBtn"
                    type="submit"
                    disabled={creating}
                  >
                    {creating ? (
                      "Зберігаю…"
                    ) : (
                      <><span className="fgd-saveDot" />Зберегти поле</>
                    )}
                  </button>
                </div>
              </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}